/**
 * Motion review switches, read from the page URL: `?slow[=N]`, `?snap=0|1`,
 * `?lite` and `?motiontrace`. They only take effect when the server enabled
 * them (filter `dhsv100_ai_chat_debug_motion`, default WP_DEBUG); on a
 * production site the URL is ignored.
 */

export interface MotionDebugFlags {
	/** Time stretch for review, 1 = real time. */
	slow: number;
	/** `false` for `?snap=0`. */
	snap: boolean;
	/** Force the lite (no blur / reduced effects) path. */
	lite: boolean;
	/** Log motion events to the console. */
	trace: boolean;
}

export const MOTION_DEBUG_DEFAULTS: Readonly< MotionDebugFlags > = {
	slow: 1,
	snap: true,
	lite: false,
	trace: false,
};

const SLOW_DEFAULT = 4;
const SLOW_MAX = 20;

/**
 * Whether the server enabled the motion debug switches: the host container
 * carries `data-debug-motion="1"`, the iframe gets `debugMotion` in its
 * options.
 */
export function isMotionDebugEnabled(): boolean {
	if ( typeof window === 'undefined' ) {
		return false;
	}
	const options = (
		window as unknown as {
			dhsv100AiChatPluginOptions?: { debugMotion?: boolean };
		}
	 ).dhsv100AiChatPluginOptions;
	if ( options?.debugMotion === true ) {
		return true;
	}
	return !! document.querySelector( '[data-debug-motion="1"]' );
}

/**
 * Parse the debug switches from a query string. Pure; ignores the enable flag.
 * @param search A query string, with or without the leading `?`.
 */
export function parseMotionDebugFlags( search: string ): MotionDebugFlags {
	const params = new URLSearchParams( search );
	const flags: MotionDebugFlags = { ...MOTION_DEBUG_DEFAULTS };

	if ( params.has( 'slow' ) ) {
		const n = Number.parseFloat( params.get( 'slow' ) ?? '' );
		flags.slow =
			Number.isFinite( n ) && n >= 1
				? Math.min( n, SLOW_MAX )
				: SLOW_DEFAULT;
	}
	if ( params.get( 'snap' ) === '0' ) {
		flags.snap = false;
	}
	if ( params.has( 'lite' ) && params.get( 'lite' ) !== '0' ) {
		flags.lite = true;
	}
	if ( params.has( 'motiontrace' ) && params.get( 'motiontrace' ) !== '0' ) {
		flags.trace = true;
	}
	return flags;
}

/**
 * The active motion debug flags: the defaults unless debugging is enabled.
 * @param search  Query string to read (defaults to `location.search`).
 * @param enabled Override of the enable check (for tests).
 */
export function getMotionDebugFlags(
	search: string = typeof window === 'undefined'
		? ''
		: window.location.search,
	enabled: boolean = isMotionDebugEnabled()
): MotionDebugFlags {
	return enabled
		? parseMotionDebugFlags( search )
		: { ...MOTION_DEBUG_DEFAULTS };
}
